import React from "react";
import defaultAxios, { AxiosError } from "axios";
import { create } from "zustand";
import Swal from "sweetalert2";
import {
    LogoutResponse,
    LoginBody,
    LoginResponse,
    UserResponse,
    RegistrasiForm,
    RegistrasiResponse,
} from "../constants/types";
import axios from "../services/axios";
import { queryClient } from "../config/queryClient";
import { getLocalUkmId, setLocalUkmId } from "../utils/handleChangeUkmId";

const initialLoginCount = Number(localStorage.getItem("loginCount")) || 0;

interface AuthStore {
    isAuthenticated: boolean;
    isLoading: boolean;
    isRegistered: boolean;
    isPreparingApp: boolean;
    ukmIdUser: number[] | undefined;
    loginCount: number;

    checkToken: (signal?: AbortSignal) => Promise<void>;
    login: (values: LoginBody) => Promise<void>;
    logout: () => Promise<void>;
    register: (values: RegistrasiForm) => Promise<void>;
    handleRegistered: () => void;
    handleAuthenticated: (value: boolean) => void;
    setRegistered: React.Dispatch<React.SetStateAction<boolean>>;
    resetLoginCount: () => void;
    loadResourcesAndDataAsync: () => Promise<void>;
}

const showSwal = (title: string, text: string) => {
    Swal.fire({
        title,
        text,
        icon: "error",
        confirmButtonColor: "#45A779",
        customClass: {
            container: "my-swal",
        },
    });
};

export const useAuthStore = create<AuthStore>((set, get) => {
    const handleErrorResponse = (error: unknown) => {
        if (!defaultAxios.isAxiosError(error)) return;
        const serverError = error as AxiosError<any>;
        if (!serverError.response) {
            console.error("", "Terjadi kesalahan! Silahkan coba lagi.", [
                { text: "OK" },
            ]);
            return;
        }

        const { loginCount } = get();
        if (loginCount >= 5) {
            showSwal(
                "Batas upaya masuk tercapai!",
                "Anda harus menunggu 5 menit sebelum bisa mencoba masuk kembali",
            );
        } else if (serverError.response.status === 400) {
            showSwal(
                "Terjadi Kesalahan!",
                `${serverError.response.data.data.errors.message}`,
            );
            const jumlahGagal = serverError.response.data.data.jumlah_gagal;
            set({ loginCount: jumlahGagal });
            localStorage.setItem("loginCount", String(jumlahGagal));
        }

        if (serverError.response.status === 422) {
            showSwal("Terjadi Kesalahan!", "Ada error validasi");
        }

        if (serverError.response.status === 403) {
            showSwal(
                "Terjadi Kesalahan!",
                `${serverError.response.data.message}`,
            );
        }
    };

    return {
        isAuthenticated: false,
        isLoading: false,
        isRegistered: false,
        isPreparingApp: true,
        ukmIdUser: undefined,
        loginCount: initialLoginCount,

        checkToken: async (signal) => {
            try {
                const { data: user } = await axios.get<UserResponse>(
                    "/api/users",
                    { params: { isNaikKelas: 1 }, signal },
                );

                const ukmId = getLocalUkmId(user?.data?.userId);
                if (ukmId) {
                    set({ ukmIdUser: [ukmId] });
                } else {
                    set({ ukmIdUser: user.data.ukmId });
                    setLocalUkmId(user.data.userId, user.data.ukmId[0]);
                }
                set({ isAuthenticated: true });
            } catch (error) {
                if (signal?.aborted || defaultAxios.isCancel(error)) {
                    return;
                }
                handleErrorResponse(error);
                set({ isAuthenticated: false });
                queryClient.clear();
            }
        },

        login: async (values) => {
            set({ isLoading: true });
            try {
                const { data } = await axios.post<LoginResponse>("/api/login", {
                    ...values,
                    isNaikKelas: 1,
                });

                const { data: user } = await axios.get<UserResponse>(
                    "/api/users",
                    { params: { isNaikKelas: 1 } },
                );

                const ukmId = getLocalUkmId(user?.data?.userId);
                if (ukmId) {
                    set({ ukmIdUser: [ukmId] });
                } else {
                    set({ ukmIdUser: user.data.ukmId });
                    setLocalUkmId(user.data.userId, user.data.ukmId[0]);
                }
                if (data.code === 200) {
                    set({ isAuthenticated: true });
                    localStorage.removeItem("loginCount");
                }
                set({ isLoading: false });
            } catch (error) {
                set({ isLoading: false });
                handleErrorResponse(error);
            }
        },

        register: async (values) => {
            set({ isLoading: true });
            try {
                const formData = new FormData();
                formData.append("nama", values.nama);
                formData.append("username", values.username);
                formData.append("email", values.email);
                formData.append("no_telp", values.no_telp);
                formData.append("password", values.password);
                formData.append("role", "10");
                formData.append("isNaikKelas", "1");
                const { data } = await axios.post<RegistrasiResponse>(
                    "/api/register",
                    formData,
                    {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    },
                );

                if (data.code === 200) {
                    await get().checkToken();
                }
                set({ isLoading: false });
            } catch (error) {
                set({ isLoading: false });
                handleErrorResponse(error);
            }
        },

        logout: async () => {
            set({ isLoading: true });
            try {
                const { data } = await axios.get<LogoutResponse>("/api/logout");
                if (data.code === 200) {
                    set({ isAuthenticated: false });
                    queryClient.clear();
                    localStorage.removeItem("popupShownAfterLogin");
                }
                set({ isLoading: false });
            } catch (error) {
                handleErrorResponse(error);
                set({ isLoading: false });
            }
        },

        handleRegistered: () => set({ isRegistered: true }),
        handleAuthenticated: (value) => set({ isAuthenticated: value }),
        setRegistered: (value) => {
            if (typeof value === "function") {
                set((state) => ({ isRegistered: value(state.isRegistered) }));
            } else {
                set({ isRegistered: value });
            }
        },
        resetLoginCount: () => {
            set({ loginCount: 0 });
            localStorage.removeItem("loginCount");
        },

        loadResourcesAndDataAsync: async () => {
            await get().checkToken();
            set({ isPreparingApp: false });
        },
    };
});
